import {
  ActionIcon,
  AppShell,
  Badge,
  Button,
  Container,
  Group,
  Indicator,
  Menu,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { TenantMark } from '@/modules/auth/TenantMark';
import { HelpButton } from '@/modules/help/HelpButton';
import { NotificationBell } from '@/modules/notifications/NotificationBell';
import { SearchPalette } from '@/modules/search/SearchPalette';
import { useSearchShortcut } from '@/modules/search/useSearchShortcut';
import { logout } from '@/shared/api/auth';
import { appearanceQueryOptions } from '@/shared/branding/appearance';
import { Icon, type IconName } from '@/shared/components/Icon';
import { NewVersionBar } from '@/shared/components/NewVersionBar';
import { OfflineBanner } from '@/shared/components/OfflineBanner';
import { PageOutlet } from '@/shared/components/PageOutlet';
import { hasConsoleAccess } from '@/shared/navigation/manifest';
import { leaveSignedOut, meQueryOptions, sessionQueryOptions } from '@/shared/session/session';
import { useSessionExpiry } from '@/shared/session/useSessionExpiry';

import classes from './PortalLayout.module.css';
import { pendingApprovalsQueryOptions } from './portalApprovals';

/**
 * The portal's shell (13 §3.2). Deliberately unlike the console's: no side menu, content centred
 * at 960 px for legibility, no tenant selector and no timer. The tenant's mark and name lead,
 * because this is the surface the customer associates with the provider's brand (`13` §3.2, "más
 * protagónico acá que en la consola") - `TenantMark` (`AM-174`) shows the real logo here the same
 * way the console header does, once `/tenant-settings/appearance` says there is one; the support
 * contact of `/admin/branding` (`AM-173`) rides alongside it, when the tenant set one. The search
 * palette (`AM-132 point 10–11`) is the same component the console uses, scoped by the API to what
 * this actor may open - their own requests and `customer` articles. The notification bell is the
 * console's as well, in its portal variant: the count of what is unread, the recent notices, and
 * a link only to the portal's own pages.
 */
export function PortalLayout() {
  const { t } = useTranslation('portal');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const session = useQuery(sessionQueryOptions());
  const me = useQuery(meQueryOptions());
  useSessionExpiry();
  const hasTenant = session.data?.activeTenant !== null && session.data?.activeTenant !== undefined;
  const appearance = useQuery({ ...appearanceQueryOptions(), enabled: hasTenant });
  const [searchOpened, { open: openSearch, close: closeSearch }] = useDisclosure(false);
  // A phone (`sm` breakpoint of the console): the entries of the header shrink to their icon.
  const phone = useMediaQuery('(max-width: 48em)', false, { getInitialValueInEffect: false });
  // An agent reaches the portal from "Mis solicitudes" in the console; without this entry the only
  // way back was typing the address. A requester whose permissions open nothing in the console
  // never sees it (13 §2.2).
  const canUseConsole =
    session.data !== undefined &&
    session.data !== null &&
    hasConsoleAccess(new Set(session.data.permissions));
  // "Mis tickets" is there for whoever may read a ticket of their own at all: a requester's role
  // that does not carry `tickets.view_own` would only find a refusal behind it.
  const canReadTickets =
    session.data !== undefined &&
    session.data !== null &&
    session.data.permissions.some((key) => TICKET_READ_KEYS.includes(key));
  // "Aprobaciones" is there for whoever may decide a step (`AM-238`): the approver of the
  // customer's side decides in the portal. The count is the same list the page shows, one query
  // of the caller's own pending decisions, shared with the page through its cache.
  const canApprove =
    session.data !== undefined &&
    session.data !== null &&
    session.data.permissions.includes('approvals.act');
  const pendingApprovals = useQuery({ ...pendingApprovalsQueryOptions(), enabled: canApprove });
  const pendingCount = pendingApprovals.data?.items.length ?? 0;

  useSearchShortcut(openSearch);

  const signOut = async () => {
    try {
      await logout();
    } finally {
      await leaveSignedOut(queryClient, () => navigate({ to: '/login' }));
    }
  };

  const tenantName = appearance.data?.displayName ?? session.data?.activeTenant?.tenantName ?? '';
  const tenantSlug = session.data?.activeTenant?.tenantSlug;
  const person =
    me.data === undefined ? '' : `${me.data.firstName ?? ''} ${me.data.lastName ?? ''}`.trim();
  const supportContact = appearance.data?.supportContact ?? null;

  return (
    <AppShell header={{ height: 56 }} padding={0}>
      <AppShell.Header>
        <Container size={960} h="100%">
          <Group h="100%" justify="space-between" wrap="nowrap" style={{ minWidth: 0 }}>
            {/* Con un nombre de tenant largo y el contacto de soporte completo, los tres textos no
                entraban en los 960 px del encabezado y cada uno se partía en varias líneas en vez de
                achicarse (revisión del manual, hallazgo D). Ronda 4, punto 6: el nombre del tenant
                cede al final (`PortalLayout.module.css`), el título entra entero y el contacto de
                soporte solo aparece si hay lugar para leerlo; no queda ningún fragmento suelto. */}
            <Group
              gap="sm"
              wrap="nowrap"
              className={classes.left}
              style={{ minWidth: 0, flex: '1 1 auto' }}
            >
              <UnstyledButton
                onClick={() => void navigate({ to: '/portal' })}
                className={classes.tenant}
              >
                <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                  <TenantMark
                    name={tenantName}
                    tenantSlug={tenantSlug}
                    hasLogo={appearance.data?.hasLogo}
                    logoVersion={appearance.data?.logoVersion}
                    size={28}
                  />
                  <Text fw={600} truncate="end" title={tenantName} style={{ minWidth: 0 }}>
                    {tenantName.length === 0 ? t('title') : tenantName}
                  </Text>
                </Group>
              </UnstyledButton>
              <Text c="dimmed" size="sm" visibleFrom="sm" className={classes.title}>
                {t('title')}
              </Text>
              {supportContact !== null && supportContact.length > 0 ? (
                <Text
                  c="dimmed"
                  size="sm"
                  truncate="end"
                  title={t('support', { contact: supportContact })}
                  className={classes.support}
                >
                  {t('support', { contact: supportContact })}
                </Text>
              ) : null}
            </Group>
            <Group wrap="nowrap" className={classes.actions} style={{ flex: 'none' }}>
              <UnstyledButton
                aria-label={t('nav.search')}
                onClick={openSearch}
                style={{ padding: '4px 8px', borderRadius: 'var(--sl-radius-1)' }}
              >
                <Icon name="search" size={18} />
              </UnstyledButton>
              <HelpButton area="portal" />
              {hasTenant ? <NotificationBell variant="portal" /> : null}
              {canReadTickets ? (
                <HeaderEntry
                  icon="ticket"
                  label={t('nav.myTickets')}
                  compact={phone}
                  onClick={() => void navigate({ to: '/portal/tickets' })}
                />
              ) : null}
              {canApprove ? (
                <HeaderEntry
                  icon="checkCircle"
                  label={t('nav.approvals')}
                  compact={phone}
                  count={pendingCount}
                  onClick={() => void navigate({ to: '/portal/approvals' })}
                />
              ) : null}
              {phone ? null : (
                <HeaderEntry
                  icon="book"
                  label={t('nav.knowledge')}
                  compact
                  onClick={() => void navigate({ to: '/portal/knowledge' })}
                />
              )}
              <HeaderEntry
                icon="plus"
                label={t('nav.new')}
                compact={phone}
                primary
                onClick={() => void navigate({ to: '/portal/new' })}
              />
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <UnstyledButton aria-label={person}>
                    <Text size="sm">{initials(person)}</Text>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{person}</Menu.Label>
                  {canUseConsole ? (
                    <Menu.Item onClick={() => void navigate({ to: '/' })}>
                      {t('nav.console')}
                    </Menu.Item>
                  ) : null}
                  <Menu.Item onClick={() => void navigate({ to: '/portal/account/security' })}>
                    {t('auth:menu.security')}
                  </Menu.Item>
                  <Menu.Item onClick={() => void signOut()}>{t('auth:menu.signOut')}</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </Container>
      </AppShell.Header>

      <AppShell.Main style={{ background: 'var(--sl-paper)' }}>
        <NewVersionBar />
        <OfflineBanner />
        <Container size={960} py="xl">
          <PageOutlet />
        </Container>
      </AppShell.Main>

      <SearchPalette opened={searchOpened} onClose={closeSearch} variant="portal" />
    </AppShell>
  );
}

/**
 * One entry of the header. With room it is a button with its icon and label; `compact` reduces it
 * to the icon alone (the label stays as its accessible name and its tooltip). Ronda 4, punto 6: the
 * seven labelled entries needed more than the 960 px of the header and left the tenant's name one
 * letter wide, and on a phone they ran off the screen.
 */
function HeaderEntry({
  icon,
  label,
  onClick,
  compact = false,
  primary = false,
  count = 0,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  compact?: boolean;
  primary?: boolean;
  /** Pending items to show next to the entry (approvals to decide). */
  count?: number;
}) {
  if (compact) {
    return (
      <Tooltip label={label} withinPortal>
        <Indicator label={count} size={16} color="orange" disabled={count === 0} offset={4}>
          <ActionIcon
            size="lg"
            variant={primary ? 'filled' : 'subtle'}
            aria-label={label}
            onClick={onClick}
          >
            <Icon name={icon} size={18} />
          </ActionIcon>
        </Indicator>
      </Tooltip>
    );
  }
  return (
    <Button
      size="sm"
      variant={primary ? 'filled' : 'subtle'}
      leftSection={<Icon name={icon} size={16} />}
      rightSection={
        count > 0 ? (
          <Badge size="sm" circle color="orange">
            {count}
          </Badge>
        ) : null
      }
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

/** Any key that reads tickets reaches the ones the person asked for (07 §3.2). */
const TICKET_READ_KEYS = [
  'tickets.view_own',
  'tickets.view_team',
  'tickets.view_organization',
  'tickets.view_all',
  'tickets.manage',
];

function initials(person: string): string {
  const parts = person.split(' ').filter((part) => part.length > 0);
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
